import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import Config from '../../Config';
import { toast } from 'react-toastify';
import Tag from '../common/Tag';

const initialState = {
  title: '',
  category: '',
  topic: '',
  type: '',
  property: {
    startText: '',
    endText: '',
    advice: { yes: '', no: '' },
    choices: [],
  },
};

const QuestionForm = ({
  question,
  onSubmit = () => {},
  type = 'add',
  currentUser,
}) => {
  const [newQuestion, setNewQuestion] = useState(initialState);
  const [topics, setTopoics] = useState([]);
  const [choice, setChoice] = useState('');
  const [choiceScore, setChoiceScore] = useState('');
  const [category] = useState([
    {
      name: 'Snel',
      value: 'quick',
    },
    {
      name: 'Uitgebreid',
      value: 'extensive',
    },
  ]);
  const [types] = useState([
    {
      name: 'Bereik',
      value: 'range',
    },
    {
      name: 'Ja/Nee',
      value: 'yes/no',
    },
    {
      name: 'Multiple choice',
      value: 'multiple',
    },
  ]);

  const fetchTopics = async () => {
    try {
      const {
        data: { data },
      } = await Axios.get(
        `${Config.apiUrl}/api/v1/survey-topics/all?organization=${currentUser?.organization?._id}`,
      );

      setTopoics(data);
    } catch (error) {
      console.log('errr', error);
      toast('Failed to fetch data. Please try again later.', {
        type: 'error',
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate(newQuestion)) return;

    onSubmit(newQuestion, setNewQuestion);
  };

  const validate = (question) => {
    const { topic } = question;
    let message = '';

    if (!topic) {
      message = 'Please select a topic';
    }

    if (message) {
      toast(message, {
        type: 'error',
      });
    }

    return message.length === 0;
  };

  const onTypeChange = (event) => {
    setNewQuestion({
      ...newQuestion,
      type: event.target.value,
      property: {
        startText: '',
        endText: '',
        advice: { yes: '', no: '' },
        choices: [],
      },
    });

    setChoice('');
  };

  const onAddChoice = () => {
    const formattedChoice = choice.trim();

    if (!formattedChoice) {
      alert('Antwoordtekst mag niet leeg zijn.');
      return;
    }

    const choiceExists = newQuestion.property.choices.some(
      (existingChoice) =>
        (typeof existingChoice === 'string' ? existingChoice : existingChoice.text).toLowerCase() === formattedChoice.toLowerCase()
    );

    if (choiceExists) {
      toast('Choice already exist', { type: 'info' });
      return;
    }

    const newChoiceObject = {
      text: formattedChoice,
    };

    const scoreValue = choiceScore.toString().trim();

    if (scoreValue !== '' && scoreValue !== '0') {
      const score = Number(scoreValue);
      if (!isNaN(score) && score >= 1 && score <= 10) {
        newChoiceObject.score = score;
      } else {
        alert('Score moet een getal tussen 1 en 10 zijn, of leeg/0 gelaten worden.');
        return;
      }
    }

    setNewQuestion({
      ...newQuestion,
      property: {
        ...newQuestion.property,
        choices: [...newQuestion.property.choices, newChoiceObject],
      },
    });
    setChoice('');
    setChoiceScore('');
  };

  const onRemoveChoice = (choiceToRemove) => {
    const updatedChoices = newQuestion.property.choices.filter((c) => {
      const choiceText = typeof c === 'string' ? c : c.text;
      const removeText = typeof choiceToRemove === 'string' ? choiceToRemove : choiceToRemove.text;
      return choiceText !== removeText;
    });

    setNewQuestion({
      ...newQuestion,
      property: { ...newQuestion.property, choices: updatedChoices },
    });
  };

  const onUpdateChoice = (index, field, value) => {
    const updatedChoices = [...newQuestion.property.choices];
    if (field === 'score') {
      if (value === '') {
        delete updatedChoices[index].score;
      } else {
        const score = parseInt(value, 10);
        if (!isNaN(score)) {
          updatedChoices[index].score = score;
        }
      }
    } else {
      updatedChoices[index][field] = value;
    }
    setNewQuestion({
      ...newQuestion,
      property: {
        ...newQuestion.property,
        choices: updatedChoices,
      },
    });
  };

  const handleExcludeFromReportChange = (e) => {
    const isChecked = e.target.checked;
    const updatedQuestion = { ...newQuestion };

    if (isChecked) {
      const updatedChoices = updatedQuestion.property.choices.map(choice => {
        const newChoice = typeof choice === 'string' ? { text: choice } : { ...choice };
        newChoice.score = 0;
        return newChoice;
      });
      updatedQuestion.property.choices = updatedChoices;
    } else {
      const updatedChoices = updatedQuestion.property.choices.map(choice => {
        const newChoice = typeof choice === 'string' ? { text: choice } : { ...choice };
        delete newChoice.score;
        return newChoice;
      });
      updatedQuestion.property.choices = updatedChoices;
    }
    setNewQuestion(updatedQuestion);
  };

  const allScoresAreZero = newQuestion.property?.choices?.length > 0 && newQuestion.property.choices.every(c => c.score === 0);

  useEffect(() => {
    setNewQuestion(question || initialState);
  }, [question]);

  useEffect(() => {
    fetchTopics();
    // eslint-disable-next-line
  }, []);

  // Initialize question data properly for updates
  useEffect(() => {
    if (question && type === 'update') {
      // Ensure choices are in the correct object format for multiple choice questions
      if (question.type === 'multiple' && question.property?.choices) {
        const formattedChoices = question.property.choices.map(choice => {
          // If choice is already an object with text and score, keep it
          if (typeof choice === 'object' && choice.text !== undefined && choice.score !== undefined) {
            return choice;
          }
          // If choice is a string, convert to object format with default score
          if (typeof choice === 'string') {
            return { text: choice, score: 5 };
          }
          return choice;
        });
        
        setNewQuestion({
          ...question,
          property: {
            ...question.property,
            choices: formattedChoices
          }
        });
      } else {
        setNewQuestion(question);
      }
    }
  }, [question, type]);

  return (
    <form
      className={`grid grid-cols-2 gap-4 ${
        type !== 'update' && 'w-full md:w-[600px]'
      }`}
      onSubmit={handleSubmit}
    >
      <input
        className="w-full text-black rounded input bg-transparent"
        placeholder="Title"
        required
        value={newQuestion?.title || ''}
        onChange={(event) =>
          setNewQuestion({ ...newQuestion, title: event.target.value })
        }
      />
      <select
        className="select rounded"
        value={newQuestion?.category}
        onChange={(event) =>
          setNewQuestion({ ...newQuestion, category: event.target.value })
        }
        required
      >
        <option value="">Selecteer vragenlijst</option>
        {category.map((item, index) => (
          <option key={index} value={item.value}>
            {item.name}
          </option>
        ))}
      </select>
      <select
        className="select rounded"
        value={newQuestion?.topic}
        onChange={(event) =>
          setNewQuestion({ ...newQuestion, topic: event.target.value })
        }
        required
      >
        <option value="">Selecteer onderwerp</option>
        {topics.map((item, index) => (
          <option key={index} value={item._id}>
            {item.title}
          </option>
        ))}
      </select>
      <select
        className="select rounded"
        value={newQuestion?.type}
        onChange={(event) => onTypeChange(event)}
        required
      >
        <option value="">Selecteer type</option>
        {types.map((item, index) => (
          <option key={index} value={item.value}>
            {item.name}
          </option>
        ))}
      </select>
      {!newQuestion?.type ||
        (newQuestion?.type === 'range' && (
          <>
            <input
              className="w-full text-black rounded input bg-transparent"
              placeholder="Start tekst"
              required
              value={newQuestion.property?.startText || ''}
              onChange={(event) =>
                setNewQuestion({
                  ...newQuestion,
                  property: {
                    ...newQuestion.property,
                    startText: event.target.value,
                  },
                })
              }
            />
            <input
              className="w-full text-black rounded input bg-transparent"
              placeholder="Eind tekst"
              required
              value={newQuestion.property?.endText || ''}
              onChange={(event) =>
                setNewQuestion({
                  ...newQuestion,
                  property: {
                    ...newQuestion.property,
                    endText: event.target.value,
                  },
                })
              }
            />
          </>
        ))}

      {newQuestion?.type && newQuestion?.type === 'yes/no' && (
        <>
          <input
            className="w-full text-black rounded input bg-transparent col-span-2"
            placeholder="Advies voor ja"
            required
            value={newQuestion.property?.advices?.yes || ''}
            onChange={(event) =>
              setNewQuestion({
                ...newQuestion,
                property: {
                  ...newQuestion.property,
                  advices: {
                    ...(newQuestion.property?.advices || {}),
                    yes: event.target.value,
                  },
                },
              })
            }
          />
          <input
            className="w-full text-black rounded input bg-transparent col-span-2"
            placeholder="Advies voor nr"
            required
            value={newQuestion.property?.advices?.no || ''}
            onChange={(event) =>
              setNewQuestion({
                ...newQuestion,
                property: {
                  ...newQuestion.property,
                  advices: {
                    ...(newQuestion.property?.advices || {}),
                    no: event.target.value,
                  },
                },
              })
            }
          />
        </>
      )}

      {newQuestion?.type && newQuestion?.type === 'multiple' && (
        <>
          <div className="col-span-2">
            <div className="flex items-center my-4">
              <input
                type="checkbox"
                id="excludeFromReport"
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                checked={allScoresAreZero}
                onChange={handleExcludeFromReportChange}
              />
              <label htmlFor="excludeFromReport" className="ml-2 block text-sm text-gray-900">
                Tel deze vraag niet mee in de rapportage
              </label>
            </div>
          </div>

          <div className="col-span-2 flex flex-row gap-2">
            <input
              placeholder="Keuze invoeren"
              className="flex-1 text-black rounded input bg-transparent"
              value={choice}
              onChange={(event) => setChoice(event.target.value)}
            />
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Score (1-10)</label>
              <input
                type="number"
                min="0"
                max="10"
                placeholder="Score"
                className="w-20 text-black rounded input bg-transparent text-center"
                value={choiceScore}
                onChange={(event) => setChoiceScore(event.target.value)}
                disabled={allScoresAreZero}
              />
            </div>
            <button
              type="button"
              className="auth-button float-right"
              onClick={onAddChoice}
            >
              +
            </button>
          </div>

          {newQuestion?.property?.choices?.length > 0 && (
            <div className="col-span-2">
              <div className="mb-2 text-sm font-medium text-gray-700">Antwoordopties:</div>
              <div className="space-y-2">
                {newQuestion?.property?.choices?.map((choice, index) => {
                  const choiceText = typeof choice === 'string' ? choice : choice.text;
                  const choiceScore = typeof choice === 'string' ? 5 : choice.score;
                  
                  const updateChoiceScore = (newScore) => {
                    onUpdateChoice(index, 'score', newScore);
                  };
                  
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <span className="text-gray-900">{choiceText}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <label className="text-xs text-gray-600 mb-1">Score</label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            className="w-16 text-black rounded input bg-white text-center text-sm"
                            value={choiceScore}
                            onChange={(event) => updateChoiceScore(event.target.value)}
                            disabled={allScoresAreZero}
                          />
                        </div>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800 font-bold"
                          onClick={() => onRemoveChoice(choice)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
              })}
              </div>
            </div>
          )}
        </>
      )}

      <button type="submit" className="auth-button mb-4">
        {type === 'update' ? 'Update' : 'Toevoegen'}
      </button>
    </form>
  );
};

export default QuestionForm;
